import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlayCircle, Truck } from "lucide-react";

export default function StartDeliveryModal({ delivery, drivers = [], open, onClose }) {
  const qc = useQueryClient();
  const [driverId, setDriverId] = useState("");

  useEffect(() => {
    if (delivery) setDriverId(delivery.delivery_person_id || "");
  }, [delivery]);

  const start = useMutation({
    mutationFn: () => api.post(`/deliveries/${delivery.id}/start`, { delivery_person_id: driverId }),
    onSuccess: () => {
      toast.success("Delivery started — dispatch time recorded");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["drivers"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!delivery) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="start-delivery-dialog">
        <DialogHeader>
          <DialogTitle>Start Delivery</DialogTitle>
          <DialogDescription>Assign a delivery person. The dispatch start time is recorded now.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-secondary/50 p-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{delivery.customer_name}</span></div>
            <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono">{formatINR(delivery.total_amount)}</span></div>
          </div>
          <div className="space-y-2">
            <Label>Delivery Person</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="start-driver-select"><SelectValue placeholder="Select delivery person" /></SelectTrigger>
              <SelectContent>
                {drivers.map((dr) => (
                  <SelectItem key={dr.id} value={dr.id}>
                    <span className="flex items-center gap-2"><Truck className="h-3.5 w-3.5" />{dr.name} — {dr.vehicle}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => start.mutate()} disabled={!driverId || start.isPending} data-testid="start-delivery-confirm">
            <PlayCircle className="mr-2 h-4 w-4" /> Start Delivery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
